import { MonoBehavior } from 'game/core/monoBehavior';
import { Store } from 'game/store/gameStore';
import { GameName } from 'constants/difficulty';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { isIOS } from 'game/core/display';
import { translate } from 'game/translate';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';
import klondikeTutorial from 'game/store/tutorial/klondikeTutorial';
import spiderTutorial from 'game/store/tutorial/spiderTutorial';
import freeCellTutorial from 'game/store/tutorial/freeCellTutorial';
import { TutorialMoveGroup } from './TutorialMoveGroup';
import { SolitaireGame } from 'game/SolitaireGame';
import { DropCardPlace, GamePlace } from 'constants/cards';
import { Card } from 'components/Card';
import { GameBoardPlace } from 'game/GameBoardPlace';
import { loadCardsTable } from 'game/loadTable';
import { TutorialKingPopup, TutorialPopupOptions } from 'game/TutorialKingPopup';
import { TutorialUI } from './TutorialUI';
import { Graphics } from 'pixi.js';
import { showPopup } from 'game/showPopup';
import { KingImages } from 'constants/tutorialKingImages';
import { GameLog } from 'game/helpers/debug';
import gsap from 'gsap';
import { SoundManager } from 'game/core/soundManager';
const HINT_DELAY = 3000; // 10 second delay before showing the hint
const FREE_PLAY_HINT_DELAY = 10000; // 10 second delay before showing the hint

export class TutorialManager extends MonoBehavior {
    tutorialMoveGroup?: TutorialMoveGroup;
    game: SolitaireGame;
    showHintTime: number = Infinity;
    moveGroup = [] as string[];
    target?: GameBoardPlace; // Default target place, can be changed based on the tutorial step
    freePlay = false;
    freePlayCondition = undefined as undefined | ((game: SolitaireGame) => boolean);
    tutorialUI: TutorialUI;
    blockHint = false; // Flag to block hint showing when the user is in free play mode
    overlayGraphics?: Graphics;
    kingPopup?: TutorialKingPopup;
    blockStockPile = true;
    tutorialCompleteTimeout?: NodeJS.Timeout;
    overlayTween?: gsap.core.Tween;

    constructor(game: SolitaireGame) {
        super();
        this.game = game;
        GameEvent.add(EventName.GAME_INTRO_COMPLETED, () => {
            if (Store.gameWins[Store.name] > 0 || Store.tutorialViewed[Store.name]) return;
            this.showWelcomeMessage.bind(this);
        });
        GameEvent.add(EventName.CLOSE_MODAL, this.onCloseModal.bind(this));
        GameEvent.add(EventName.RESTART_TUTORIAL, () => {
            this.tutorialMoveGroup?.destroy();
            this.overlayGraphics?.destroy();
            this.overlayGraphics?.removeFromParent();
            this.overlayGraphics = undefined;
            Store.setGameTutorialStep(0);
            GameEvent.emit(EventName.START_TUTORIAL);
        });
        GameEvent.add(EventName.STOP_TUTORIAL, () => {
            this.game.cards.forEach((card) => {
                card.inTutorial = false; // Reset inTutorial flag for all cards
                card.container.zIndex = card.place.zIndex; // Reset zIndex to original place
            });
            if (this.kingPopup) this.removeKingPopup();
            this.tutorialMoveGroup?.destroy();
            this.clearOverlay();
            this.tutorialUI.hide();
        });
        GameEvent.add(EventName.START_TUTORIAL, () => {
            this.tutorialUI.show();
        });
        this.tutorialUI = new TutorialUI();
    }
    createTutorialView() {
        this.loadTutorialGameStep(Store.tutorialStep);
        loadCardsTable(this.game, () => {
            GameEvent.emit(EventName.UNBLOCK_KEYBOARD);
            setTimeout(() => {
                this.setTutorialGameStep(Store.tutorialStep);
                this.game.refreshBoard();
            }, 100);
        });
    }

    onCloseModal({ id, confirm, button }: { id: string; confirm: boolean; button: HTMLDivElement }) {
        //tutorial_start_notification_view
        switch (id) {
            case 'tutorial_start':
                if (confirm) {
                    Store.setGameState(this.game);
                    Store.setTutorialGame(Store.name);
                    Store.setTutorialActive(true);
                    GAEvent(GAEventNames.TUTORIAL_MODAL_START, {
                        number: Store.countPopupShown('tutorial_start'),
                        type: Store.getGameTypeText(),
                        difficulty: Store.getDifficultyText(),
                        placement: Store.tutorialPlacement,
                    });
                } else if (button.classList.contains('close_btn')) {
                    GAEvent(GAEventNames.TUTORIAL_MODAL_CANCEL, {
                        number: Store.countPopupShown('tutorial_start'),
                        type: Store.getGameTypeText(),
                        difficulty: Store.getDifficultyText(),
                    });
                } else this.openRules();
                break;
            case 'tutorial_completed':
            case 'tutorial_completed_spider':
                if (confirm) {
                    Store.setTutorialActive(false);
                    Store.setTutorialGame(null);

                    GAEvent(GAEventNames.TUTORIAL_COMPLETE_CONFIRM, {
                        step: Store.tutorialStep,
                        difficulty: Store.getDifficultyText(),
                        type: Store.getGameTypeText(),
                        placement: Store.tutorialPlacement,
                    });
                    if (Store.inArticle) {
                        const url = document.getElementById('game-container')?.getAttribute('data-gameUrl');
                        window.location = url || '/';
                    } else {
                        this.game.newGame(Store.difficulty, true);
                    }
                } else {
                    GameEvent.emit(EventName.RESTART_TUTORIAL);
                    GAEvent(GAEventNames.TUTORIAL_COMPLETE_RESTART, {
                        step: Store.tutorialStep,
                        difficulty: Store.getDifficultyText(),
                        type: Store.getGameTypeText(),
                        placement: Store.tutorialPlacement,
                    });
                }
                break;
            case 'tutorial_skip':
                if (confirm) {
                    GAEvent(GAEventNames.TUTORIAL_SKIP_CONFIRM, {
                        step: Store.tutorialStep,
                        difficulty: Store.getDifficultyText(),
                        type: Store.getGameTypeText(),
                    });
                    Store.setTutorialActive(false);
                    Store.setTutorialGame(null);
                    this.game.newGame(Store.difficulty, true);
                } else {
                    GAEvent(GAEventNames.TUTORIAL_SKIP_CANCEL, {
                        step: Store.tutorialStep,
                        difficulty: Store.getDifficultyText(),
                        type: Store.getGameTypeText(),
                    });
                }
                break;
            case 'tutorial_restart':
                if (confirm) {
                    GAEvent(GAEventNames.TUTORIAL_RESTART_CONFIRM, {
                        step: Store.tutorialStep,
                        difficulty: Store.getDifficultyText(),
                        type: Store.getGameTypeText(),
                        placement: Store.tutorialPlacement,
                    });
                    GameEvent.emit(EventName.RESTART_TUTORIAL);
                } else {
                    GAEvent(GAEventNames.TUTORIAL_RESTART_CANCEL, {
                        step: Store.tutorialStep,
                        difficulty: Store.getDifficultyText(),
                        type: Store.getGameTypeText(),
                        placement: Store.tutorialPlacement,
                    });
                }

                break;
        }
    }

    getTutorialData() {
        let tutorialFile;
        switch (Store.name) {
            case GameName.KLONDIKE:
                tutorialFile = klondikeTutorial;
                break;
            case GameName.SPIDER:
                tutorialFile = spiderTutorial;
                break;
            case GameName.FREECELL:
                tutorialFile = freeCellTutorial;
                break;
        }
        return tutorialFile;
    }
    loadTutorialGameStep(stepIndex: number) {
        const { step } = this.getTutorialData();
        Store.setGameTutorialStep(stepIndex, step);
    }
    setTutorialGameStep(stepIndex: number) {
        this.game.cards.forEach((card) => {
            card.inTutorial = false; // Reset inTutorial flag for all cards
            ``;
        });
        this.blockHint = false;
        if (this.tutorialMoveGroup) this.tutorialMoveGroup.destroy();
        this.showHintTime = performance.now() + (this.freePlay ? FREE_PLAY_HINT_DELAY : HINT_DELAY);

        const { step, moves, contentKeys } = this.getTutorialData();
        if (moves.length <= stepIndex || contentKeys.length <= stepIndex) {
            console.warn('No more tutorial steps available');
            return;
        }
        this.moveGroup = moves[stepIndex].cards;
        if (this.moveGroup.length === 0) {
            this.blockHint = true; // If no cards to move, block hints
        }
        this.target = this.findGamePlaceBoard(moves[stepIndex].target);

        this.freePlay = moves[stepIndex].freePlay || false;
        this.blockStockPile = moves[stepIndex].blockStockPile || false;

        this.freePlayCondition = moves[stepIndex].freePlayCondition || undefined;
        Store.setGameTutorialStep(stepIndex, step);

        const popupOptions: TutorialPopupOptions = {
            contentKey: contentKeys[stepIndex].contentKey,
            kingIcon: contentKeys[stepIndex].king,
        };

        if (
            this.kingPopup &&
            (this.kingPopup.kingIcon !== contentKeys[stepIndex].king ||
                this.kingPopup.stepKey !== contentKeys[stepIndex].contentKey)
        ) {
            this.removeKingPopup();
            this.kingPopup = new TutorialKingPopup(popupOptions);
        } else if (!this.kingPopup) {
            this.kingPopup = new TutorialKingPopup(popupOptions);
        }

        this.refreshBoard();
    }

    clearOverlay() {
        if (this.overlayGraphics) {
            this.overlayTween = gsap.to(this.overlayGraphics, {
                alpha: 0,
                duration: 0.5,
                ease: 'power1.inOut',
                onComplete: () => {
                    if (this.overlayGraphics) {
                        this.overlayGraphics.destroy();
                        this.overlayGraphics = undefined;
                    }
                },
            });
        }
    }
    showOverlay() {
        if (this.overlayGraphics) {
            return;
        }
        this.overlayGraphics = new Graphics();
        this.overlayGraphics.rect(-2000, -2000, 4000, 4000);
        this.overlayGraphics.fill(0x000000);
        this.overlayGraphics.alpha = 0;
        this.game.gameContainer.addChild(this.overlayGraphics);
        this.overlayGraphics.eventMode = 'static';
        this.overlayGraphics.zIndex = 2000;
        this.overlayTween = gsap.to(this.overlayGraphics, {
            alpha: 0.2,
            duration: 0.5,
            ease: 'power1.inOut',
        });
    }
    findGamePlaceBoard(target: string): GameBoardPlace {
        let gamePlace = this.game.stockPile as GameBoardPlace;
        switch (target) {
            case 'W':
                gamePlace = this.game.wastePile;
                break;
            case 'F':
                const foundation = this.game.foundations.find((foundation) => foundation.isEmpty);
                if (foundation) {
                    gamePlace = foundation;
                }
                break;
            case 'C':
                const columns = this.game.columns.filter((column) => column.isEmpty);
                if (Store.name === GameName.KLONDIKE) gamePlace = columns[columns.length - 1];
                else gamePlace = columns[0];

                break;
            case 'FR':
                const freeCell = this.game.freeCells.find((f) => f.isEmpty) || this.game.freeCells[0];
                if (freeCell) gamePlace = freeCell;
                break;
            default:
                const targetCard = this.game.cards.find((card) => card.code === target);

                if (targetCard) {
                    switch (targetCard.place.cardPlace) {
                        case GamePlace.FOUNDATION:
                            gamePlace = this.game.foundations[targetCard.place.value];
                            break;
                        case GamePlace.TABLEAU:
                            gamePlace = this.game.columns[targetCard.place.value];
                            break;
                        case GamePlace.FREECELL:
                            gamePlace = this.game.freeCells[targetCard.place.value];
                            break;
                    }
                    break;
                }
        }
        return gamePlace;
    }

    openRules() {
        GAEvent(GAEventNames.TUTORIAL_READ_RULES, {
            number: Store.countPopupShown('tutorial_start'),
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
        GAEvent(GAEventNames.GAME_RULES_OPEN, {
            timeSec: Store.time,
        });
        let url = '';
        switch (Store.name) {
            case GameName.KLONDIKE:
                url = translate('rulesKlondikeURL');
                break;
            case GameName.SPIDER:
                url = translate('rulesSpiderURL');
                break;
            case GameName.FREECELL:
                url = translate('rulesFreecellURL');
                break;
            case GameName.PYRAMID:
                url = translate('rulesPyramidURL');
            break;
        }

        if (isIOS()) window.location.href = url;
        else window.open(url, '_blank');
    }
    update(): void {
        if (!Store.tutorialActive) return; // Prevent showing hints in free play mode
        if (performance.now() > this.showHintTime) this.showHint();
    }
    showHint() {
        if (this.freePlay) {
            GameEvent.emit(EventName.HINT);
            this.showHintTime = performance.now() + FREE_PLAY_HINT_DELAY;
        } else {
            // Prevent showing hints in free play mode
            if (this.tutorialMoveGroup) this.tutorialMoveGroup.destroy();
            this.showHintTime = performance.now() + HINT_DELAY;

            this.tutorialMoveGroup = new TutorialMoveGroup(this.game, this.moveGroup, this.target);
        }
    }
    isTutorialCompleted(): boolean {
        return (
            this.game.foundations.length > 0 && //no trigger at game load
            this.game.foundations.every((foundation) => foundation.isComplete)
        );
    }
    completeTutorial() {
        SoundManager.play('winPopupSFX');
        this.blockHint = true; // Prevent showing hints on complete
        showPopup(Store.name === GameName.SPIDER ? 'tutorial_completed_spider' : 'tutorial_completed');
        this.showHintTime = Infinity; // Stop showing hints
        this.kingPopup?.remove();
        this.kingPopup = undefined;
        GameLog('Tutorial completed', {
            type: Store.getGameTypeText(),
            placement: Store.tutorialPlacement,
        });
        GAEvent(GAEventNames.TUTORIAL_COMPLETE_POPUP, {
            type: Store.getGameTypeText(),
            placement: Store.tutorialPlacement,
        });
    }
    resize(width: number, height: number): void {
        this.refreshBoard();
    }
    refreshBoard() {
        if (Store.tutorialActive) {
            if (this.isTutorialCompleted()) {
                if (this.tutorialCompleteTimeout) clearTimeout(this.tutorialCompleteTimeout);
                this.tutorialCompleteTimeout = setTimeout(() => this.completeTutorial(), 500);
                return;
            }

            if (this.blockStockPile) {
                this.game.stockPile.block();
            } else {
                this.game.stockPile.unblock();
            }
            if (this.freePlay) {
                this.clearOverlay();
            } else {
                this.showCardsAboveOverlay();
                this.showOverlay();
            }
        }
    }
    showCardsAboveOverlay() {
        this.game.cards.forEach((card) => {
            card.inTutorial = false;
            if (this.moveGroup.includes(card.code)) {
                if ([GamePlace.FREECELL, GamePlace.TABLEAU, GamePlace.WASTE_PILE].includes(card.place.cardPlace)) {
                    card.container.zIndex = 3000 + card.place.zIndex;
                    card.inTutorial = true; // Mark card as in tutorial
                }
            }
        });
    }

    isValidMove(moveGroup: Card[], gameBoardPlace: DropCardPlace): boolean {
        if (this.freePlay) return true;
        if (!this.target) return false;
        return gameBoardPlace.place === this.target.gamePlace;
    }
    onValidMove() {
        if (this.freePlay && this.freePlayCondition) {
            if (this.freePlayCondition(this.game)) {
                GAEvent(GAEventNames.TUTORIAL_STEP + (Store.tutorialStep + 1), {
                    timeSec: Store.time,
                    placement: Store.tutorialPlacement,
                });
                this.setTutorialGameStep(Store.tutorialStep + 1);
            }

            this.blockHint = true;
            this.showHintTime = performance.now() + FREE_PLAY_HINT_DELAY;
        } else {
            GAEvent(GAEventNames.TUTORIAL_STEP + (Store.tutorialStep + 1), {
                timeSec: Store.time,
                placement: Store.tutorialPlacement,
            });
            this.setTutorialGameStep(Store.tutorialStep + 1);
        }
    }
    showWelcomeMessage() {
        if (Store.inArticle) {
            return;
        } // TODO show step -1, block the game
        if (!Store.availableGamesToTutorial.includes(Store.name)) return;
        if (Store.tutorialActive) return;
        if (Store.tutorialViewed[Store.name]) return; // Already viewed the tutorial

        if (this.kingPopup) {
            this.kingPopup?.remove();
            this.kingPopup = undefined;
        }

        Store.addTutorialWelcomeScreenShowedCount();

        GAEvent(GAEventNames.TUTORIAL_WELCOME_POPUP_VIEW, {
            number: Store.tutorialWelcomeScreenShowedCount,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        this.kingPopup = new TutorialKingPopup({
            contentKey: 'startTutorialPopupTitle',
            kingIcon: KingImages.HANDS_FORWARD,
            skipButton: {
                label: 'skipTutorial',
                onClick: () => {
                    GAEvent(GAEventNames.TUTORIAL_WELCOME_POPUP_SKIP, {
                        number: Store.tutorialWelcomeScreenShowedCount,
                        type: Store.getGameTypeText(),
                        difficulty: Store.getDifficultyText(),
                    });
                    Store.setTutorialViewed();
                    this.kingPopup?.remove();
                    this.kingPopup = undefined;
                },
            },
            confirmButton: {
                label: 'startTutorial',
                onClick: () => {
                    GAEvent(GAEventNames.TUTORIAL_WELCOME_POPUP_START, {
                        number: Store.tutorialWelcomeScreenShowedCount,
                        type: Store.getGameTypeText(),
                        difficulty: Store.getDifficultyText(),
                    });
                    this.removeKingPopup();
                    Store.setTutorialGame(Store.name);
                    Store.setTutorialActive(true);
                },
            },
        });
    }
    removeKingPopup() {
        if (this.kingPopup) {
            this.kingPopup.remove();
            this.kingPopup = undefined;
        }
    }
}
